import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Bookmark, Clock, MapPin, MessageCircle, Megaphone, Send, Trash2, Rocket, Play, Image as ImageIcon, Share2, Users, Loader2, ArrowLeft, Sparkles, CheckCircle2, PartyPopper } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  powerGet, powerSign, powerBookmarkToggle, compactNumber,
  powerComments, powerComment, powerCommentDelete,
  powerUpdates, powerUpdate, powerMediaList, powerMediaAdd,
  powerPromotePlans, powerPromote,
} from "@/lib/powerApi";
import { ProgressBar } from "@/components/power/PetitionCard";
import PowerShareDialog from "@/components/power/PowerShareDialog";

export default function PowerPetition() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [showSign, setShowSign] = useState(false);
  const [showPromote, setShowPromote] = useState(false);
  const [thanks, setThanks] = useState(false);
  const [bookmarking, setBookmarking] = useState(false);

  const reload = () => powerGet(slug).then(setData).catch(() => toast.error("Petition not found"));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [slug]);

  const toggleBookmark = async () => {
    if (!user) { nav("/login?next=" + encodeURIComponent(`/power/p/${slug}`)); return; }
    setBookmarking(true);
    try {
      const r = await powerBookmarkToggle(slug);
      setData((d) => ({ ...d, bookmarked: r.bookmarked }));
      toast.success(r.bookmarked ? "Bookmarked" : "Removed from bookmarks");
    } catch { toast.error("Couldn't bookmark"); }
    finally { setBookmarking(false); }
  };

  if (!data) return <div className="py-12 text-center text-[#0A1A2F]/50">Loading petition…</div>;

  const p = data.petition;

  return (
    <div data-testid="power-detail" className="pb-16">
      <button onClick={() => nav(-1)} className="text-[#0A1A2F]/60 text-sm inline-flex items-center gap-1 mb-3 hover:text-[#1E88FF]">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="rounded-3xl overflow-hidden bg-white border border-[#E4EAF3] shadow-sm">
        <div className="relative aspect-[21/9] sm:aspect-[21/8] bg-[#0A1A2F]">
          {p.cover_url && <img src={p.cover_url} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          <div className="absolute top-3 left-3 flex gap-1.5">
            {p.is_featured && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-[#1E88FF] text-white px-2 py-1 rounded-full">
                <Sparkles className="w-3 h-3" /> Featured
              </span>
            )}
            {p.is_completed && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-[#22C55E] text-white px-2 py-1 rounded-full">
                <CheckCircle2 className="w-3 h-3" /> Victory
              </span>
            )}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#1E88FF]">{p.category}</div>
          <h1 data-testid="detail-title" className="text-2xl sm:text-4xl font-black text-[#0A1A2F] mt-1 leading-tight">{p.title}</h1>
          {p.summary && <p className="text-[#0A1A2F]/70 mt-2 text-sm sm:text-base">{p.summary}</p>}

          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[#0A1A2F]/70">
            <div className="inline-flex items-center gap-2">
              <div className="w-6 h-6 rounded-full" style={{ background: p.creator?.avatar_color || "#1E88FF" }} />
              <span className="font-semibold text-[#0A1A2F]">{p.creator?.name}</span>
              {p.creator?.verified && <BadgeCheck className="w-4 h-4 text-[#1E88FF]" fill="#1E88FF" stroke="white" />}
            </div>
            {p.location && (
              <div className="inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> {p.location}
              </div>
            )}
            {p.days_left !== null && p.days_left !== undefined && !p.is_completed && (
              <div className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {p.days_left} days left
              </div>
            )}
          </div>

          <div className="mt-5 p-4 bg-[#F6F8FC] rounded-2xl">
            <div className="flex items-end justify-between gap-3">
              <div>
                <div className="text-2xl sm:text-3xl font-black text-[#0A1A2F]" data-testid="detail-count">
                  {compactNumber(p.signature_count)}
                </div>
                <div className="text-xs text-[#0A1A2F]/60">of {compactNumber(p.goal)} signatures</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black text-[#1E88FF]">{Math.round(p.progress_pct)}%</div>
                <div className="text-xs text-[#0A1A2F]/60">to the goal</div>
              </div>
            </div>
            <ProgressBar count={p.signature_count} goal={p.goal} pct={p.progress_pct} />
            <div className="mt-4 flex flex-wrap gap-2">
              {data.signed_by_me ? (
                <div data-testid="already-signed" className="inline-flex items-center gap-2 bg-[#22C55E]/10 text-[#22C55E] font-bold px-5 py-3 rounded-full">
                  <CheckCircle2 className="w-5 h-5" /> You've signed this petition
                </div>
              ) : (
                <button data-testid="detail-sign-btn" onClick={() => setShowSign(true)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1E88FF] to-[#0A1A2F] text-white font-black px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-shadow">
                  Sign this petition
                </button>
              )}
              <button data-testid="detail-share-btn" onClick={() => setShowShare(true)}
                className="inline-flex items-center gap-2 bg-white border border-[#E4EAF3] text-[#0A1A2F] font-semibold px-5 py-3 rounded-full hover:border-[#1E88FF]">
                <Share2 className="w-4 h-4" /> Share
              </button>
              <button data-testid="detail-bookmark-btn" onClick={toggleBookmark} disabled={bookmarking}
                className={"inline-flex items-center gap-2 border font-semibold px-5 py-3 rounded-full transition-colors " + (data.bookmarked ? "bg-[#1E88FF]/10 border-[#1E88FF]/30 text-[#1E88FF]" : "bg-white border-[#E4EAF3] text-[#0A1A2F] hover:border-[#1E88FF]")}>
                <Bookmark className="w-4 h-4" fill={data.bookmarked ? "currentColor" : "none"} />
                {data.bookmarked ? "Saved" : "Save"}
              </button>
            </div>
          </div>

          <section className="mt-8">
            <h2 className="font-black text-xl text-[#0A1A2F] mb-2">The story</h2>
            <div data-testid="detail-story" className="text-[#0A1A2F]/80 leading-relaxed whitespace-pre-wrap">
              {p.story}
            </div>
          </section>

          <PetitionUpdates slug={slug} isCreator={user && p.creator?.id === user.id} />
          <PetitionMedia slug={slug} isCreator={user && p.creator?.id === user.id} />
          <PetitionComments slug={slug} user={user} />

          {user && p.creator?.id === user.id && !p.is_completed && (
            <div className="mt-6 p-4 rounded-2xl bg-gradient-to-br from-[#FBBF24]/10 to-[#1E88FF]/10 border border-[#FBBF24]/30 flex items-center gap-3">
              <Rocket className="w-6 h-6 text-[#F59E0B]" />
              <div className="flex-1 min-w-0">
                <div className="font-black text-[#0A1A2F]">Promote this petition</div>
                <div className="text-xs text-[#0A1A2F]/60">Feature on the home & category pages to reach more supporters faster.</div>
              </div>
              <button data-testid="detail-promote-btn" onClick={() => setShowPromote(true)}
                className="inline-flex items-center gap-1 bg-[#F59E0B] text-white font-bold text-xs px-4 py-2 rounded-full hover:bg-[#B45309]">
                <Sparkles className="w-3.5 h-3.5" /> Boost
              </button>
            </div>
          )}

          {data.supporters?.length > 0 && (
            <section className="mt-8">
              <h2 className="font-black text-xl text-[#0A1A2F] mb-3 inline-flex items-center gap-2">
                <Users className="w-5 h-5 text-[#1E88FF]" /> Recent supporters
              </h2>
              <div className="flex flex-wrap gap-2">
                {data.supporters.map((s, i) => (
                  <div key={i} className="bg-[#F6F8FC] rounded-full px-3 py-1.5 text-xs text-[#0A1A2F]/70">
                    <span className="font-semibold text-[#0A1A2F]">{s.first_name} {s.last_initial}</span>
                    {s.city && <span className="text-[#0A1A2F]/50"> · {s.city}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {showShare && (
        <PowerShareDialog url={window.location.href} title={p.title} onClose={() => setShowShare(false)} />
      )}
      {showSign && (
        <SignModal slug={slug} onClose={() => setShowSign(false)}
          onSuccess={() => {
            setShowSign(false);
            setThanks(true);
            reload();
            setTimeout(() => setThanks(false), 3800);
          }} />
      )}
      {thanks && <ThankYouOverlay onClose={() => setThanks(false)} />}
      {showPromote && <PromoteModal slug={slug} onClose={() => setShowPromote(false)} onDone={() => { setShowPromote(false); reload(); }} />}
    </div>
  );
}

function SignModal({ slug, onClose, onSuccess }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.name?.split(" ")[0] || "",
    last_name: user?.name?.split(" ").slice(1).join(" ") || "",
    email: user?.email || "",
    city: "",
    country: "",
    display_name: true,
    accept_terms: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) { toast.error("Please accept the terms to sign"); return; }
    setBusy(true);
    try {
      await powerSign(slug, form);
      onSuccess?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't sign");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="sign-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-[#E4EAF3]">
          <div className="font-black text-xl text-[#0A1A2F]">Add your name</div>
          <div className="text-xs text-[#0A1A2F]/60 mt-0.5">Your signature helps push this to decision-makers.</div>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input required data-testid="sign-first-name" value={form.first_name} onChange={set("first_name")} placeholder="First name"
              className="px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
            <input required data-testid="sign-last-name" value={form.last_name} onChange={set("last_name")} placeholder="Last name"
              className="px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
          </div>
          <input required data-testid="sign-email" type="email" value={form.email} onChange={set("email")} placeholder="Email"
            className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="sign-city" value={form.city} onChange={set("city")} placeholder="City"
              className="px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
            <input data-testid="sign-country" value={form.country} onChange={set("country")} placeholder="Country"
              className="px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#0A1A2F]/80">
            <input data-testid="sign-display" type="checkbox" checked={form.display_name} onChange={set("display_name")} />
            Display my name publicly on the supporters list
          </label>
          <label className="flex items-start gap-2 text-sm text-[#0A1A2F]/80">
            <input data-testid="sign-terms" type="checkbox" checked={form.accept_terms} onChange={set("accept_terms")} className="mt-0.5" />
            I understand my name & city may be shown publicly, and I accept the <Link to="/power/terms" className="text-[#1E88FF] font-semibold">Terms</Link>.
          </label>
          <button data-testid="sign-submit" type="submit" disabled={busy || !form.accept_terms}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1E88FF] to-[#0A1A2F] text-white font-black py-4 rounded-full disabled:opacity-50">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign petition"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ThankYouOverlay({ onClose }) {
  return (
    <div data-testid="thankyou-overlay" className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-8 max-w-sm text-center shadow-2xl animate-in fade-in slide-in-from-bottom-4">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#22C55E] to-[#1E88FF] flex items-center justify-center mx-auto mb-4 animate-bounce">
          <PartyPopper className="w-10 h-10 text-white" />
        </div>
        <div className="font-black text-2xl text-[#0A1A2F]">Thank you!</div>
        <div className="text-[#0A1A2F]/70 mt-1">Your signature has been recorded. Share it to double your impact.</div>
      </div>
    </div>
  );
}

// ─── Updates ────────────────────────────────────────────────────────────
function PetitionUpdates({ slug, isCreator }) {
  const [updates, setUpdates] = useState([]);
  const [show, setShow] = useState(false);
  useEffect(() => { powerUpdates(slug).then((r) => setUpdates(r.updates || [])).catch(() => {}); }, [slug]);
  if (updates.length === 0 && !isCreator) return null;
  return (
    <section data-testid="detail-updates" className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#1E88FF]/10 text-[#1E88FF] flex items-center justify-center"><Megaphone className="w-4 h-4" /></div>
        <h2 className="font-black text-xl text-[#0A1A2F]">Updates</h2>
        {isCreator && (
          <button data-testid="detail-post-update" onClick={() => setShow(true)}
            className="ml-auto inline-flex items-center gap-1 bg-[#1E88FF] text-white font-bold text-xs px-3 py-1.5 rounded-full">
            <Send className="w-3 h-3" /> Post update
          </button>
        )}
      </div>
      {updates.length === 0 ? (
        <div className="bg-[#F6F8FC] rounded-xl p-4 text-sm text-[#0A1A2F]/60">No updates yet.</div>
      ) : (
        <div className="space-y-3">
          {updates.map((u) => (
            <article key={u.id} data-testid={`upd-${u.id}`} className="bg-white rounded-xl border border-[#E4EAF3] p-4">
              <div className="font-black text-[#0A1A2F]">{u.title}</div>
              <div className="text-[11px] text-[#0A1A2F]/50">{u.author_name} · {(u.created_at || "").slice(0, 10)}</div>
              <div className="text-sm text-[#0A1A2F]/80 mt-2 whitespace-pre-wrap">{u.body}</div>
              {u.media_url && (
                <img src={u.media_url} alt="" className="mt-3 rounded-lg max-h-80 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
            </article>
          ))}
        </div>
      )}
      {show && <UpdateModal slug={slug} onClose={() => setShow(false)} onDone={(u) => { setUpdates((list) => [u, ...list]); setShow(false); }} />}
    </section>
  );
}

function UpdateModal({ slug, onClose, onDone }) {
  const [f, setF] = useState({ title: "", body: "", media_url: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await powerUpdate(slug, f);
      toast.success("Update posted");
      onDone?.(r.update);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't post"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="update-modal" className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl p-5 space-y-3">
        <div className="font-black text-lg text-[#0A1A2F]">Post an update</div>
        <input required minLength={3} maxLength={140} data-testid="update-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
          placeholder="Update title" className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:outline-none focus:border-[#1E88FF]" />
        <textarea required minLength={5} maxLength={6000} rows={5} data-testid="update-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })}
          placeholder="What's new?" className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:outline-none focus:border-[#1E88FF]" />
        <input data-testid="update-media" value={f.media_url} onChange={(e) => setF({ ...f, media_url: e.target.value })}
          placeholder="Optional image URL" className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:outline-none focus:border-[#1E88FF]" />
        <button data-testid="update-submit" type="submit" disabled={busy} className="w-full bg-gradient-to-r from-[#1E88FF] to-[#0A1A2F] text-white font-black py-3 rounded-full disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Post update"}
        </button>
      </form>
    </div>
  );
}

// ─── Media ──────────────────────────────────────────────────────────────
function PetitionMedia({ slug, isCreator }) {
  const [media, setMedia] = useState([]);
  const [show, setShow] = useState(false);
  useEffect(() => { powerMediaList(slug).then((r) => setMedia(r.media || [])).catch(() => {}); }, [slug]);
  if (media.length === 0 && !isCreator) return null;
  return (
    <section data-testid="detail-media" className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#1E88FF]/10 text-[#1E88FF] flex items-center justify-center"><ImageIcon className="w-4 h-4" /></div>
        <h2 className="font-black text-xl text-[#0A1A2F]">Photos & Videos</h2>
        {isCreator && (
          <button data-testid="detail-add-media" onClick={() => setShow(true)} className="ml-auto inline-flex items-center gap-1 bg-[#1E88FF] text-white font-bold text-xs px-3 py-1.5 rounded-full">
            <ImageIcon className="w-3 h-3" /> Add media
          </button>
        )}
      </div>
      {media.length === 0 ? (
        <div className="bg-[#F6F8FC] rounded-xl p-4 text-sm text-[#0A1A2F]/60">No media yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {media.map((m) => (
            <div key={m.id} data-testid={`media-${m.id}`} className="relative rounded-xl overflow-hidden bg-black aspect-video">
              {m.kind === "video" ? (
                <video src={m.url} controls className="w-full h-full object-cover" />
              ) : (
                <img src={m.url} alt={m.caption || ""} className="w-full h-full object-cover" />
              )}
              {m.kind === "video" && <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Play className="w-3 h-3" /> Video</div>}
            </div>
          ))}
        </div>
      )}
      {show && <MediaModal slug={slug} onClose={() => setShow(false)} onDone={(m) => { setMedia((list) => [m, ...list]); setShow(false); }} />}
    </section>
  );
}

function MediaModal({ slug, onClose, onDone }) {
  const [f, setF] = useState({ kind: "image", url: "", caption: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^https?:\/\//.test(f.url)) { toast.error("URL must start with http(s)"); return; }
    setBusy(true);
    try {
      const r = await powerMediaAdd(slug, f);
      toast.success("Media added");
      onDone?.(r.media);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't add"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="media-modal" className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl p-5 space-y-3">
        <div className="font-black text-lg text-[#0A1A2F]">Add photo or video</div>
        <div className="flex gap-2">
          {["image", "video"].map((k) => (
            <button key={k} type="button" data-testid={`media-kind-${k}`} onClick={() => setF({ ...f, kind: k })}
              className={"flex-1 py-2 rounded-xl font-semibold text-sm capitalize " + (f.kind === k ? "bg-[#1E88FF] text-white" : "bg-[#F6F8FC] border border-[#E4EAF3] text-[#0A1A2F]/70")}>
              {k}
            </button>
          ))}
        </div>
        <input required data-testid="media-url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })}
          placeholder="https://…" className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:outline-none focus:border-[#1E88FF]" />
        <input data-testid="media-caption" value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} maxLength={200}
          placeholder="Optional caption" className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:outline-none focus:border-[#1E88FF]" />
        <button data-testid="media-submit" type="submit" disabled={busy} className="w-full bg-gradient-to-r from-[#1E88FF] to-[#0A1A2F] text-white font-black py-3 rounded-full disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Add"}
        </button>
      </form>
    </div>
  );
}

// ─── Comments ──────────────────────────────────────────────────────────
function PetitionComments({ slug, user }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = () => powerComments(slug).then((r) => setComments(r.comments || [])).catch(() => {});
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to comment"); return; }
    if (!body.trim()) return;
    setBusy(true);
    try {
      const r = await powerComment(slug, body.trim());
      setComments((list) => [r.comment, ...list]);
      setBody("");
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't comment"); }
    finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await powerCommentDelete(c.id);
      setComments((list) => list.filter((x) => x.id !== c.id));
    } catch { toast.error("Couldn't delete"); }
  };

  return (
    <section data-testid="detail-comments" className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#1E88FF]/10 text-[#1E88FF] flex items-center justify-center"><MessageCircle className="w-4 h-4" /></div>
        <h2 className="font-black text-xl text-[#0A1A2F]">Comments</h2>
        <span className="text-xs text-[#0A1A2F]/50">({comments.length})</span>
      </div>

      {user ? (
        <form onSubmit={submit} className="flex gap-2 mb-4">
          <input data-testid="comment-input" value={body} onChange={(e) => setBody(e.target.value)} maxLength={1500}
            placeholder="Add a supportive comment…" className="flex-1 px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:outline-none focus:border-[#1E88FF]" />
          <button data-testid="comment-submit" type="submit" disabled={busy || !body.trim()}
            className="inline-flex items-center gap-1 bg-[#1E88FF] text-white font-bold px-4 rounded-xl disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      ) : (
        <div className="bg-[#F6F8FC] rounded-xl p-3 text-sm text-[#0A1A2F]/70 mb-4">
          <Link to={`/login?next=${encodeURIComponent(`/power/p/${slug}`)}`} className="text-[#1E88FF] font-semibold">Sign in</Link> to comment.
        </div>
      )}

      {comments.length === 0 ? (
        <div className="bg-[#F6F8FC] rounded-xl p-4 text-sm text-[#0A1A2F]/60">Be the first to comment.</div>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <li key={c.id} data-testid={`cmt-${c.id}`} className="flex gap-3">
              <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center font-black text-white" style={{ background: c.avatar_color }}>{(c.user_name || "?")[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0 bg-[#F6F8FC] rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-sm text-[#0A1A2F]">{c.user_name}</div>
                  <div className="text-[10px] text-[#0A1A2F]/50">{(c.created_at || "").slice(0, 10)}</div>
                  {user && c.user_id === user.id && (
                    <button data-testid={`cmt-del-${c.id}`} onClick={() => del(c)} className="ml-auto text-[#0A1A2F]/40 hover:text-[#FF3B30]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="text-sm text-[#0A1A2F]/80 mt-1 whitespace-pre-wrap">{c.body}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Promotion checkout (MOCK) ─────────────────────────────────────────
function PromoteModal({ slug, onClose, onDone }) {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { powerPromotePlans().then((r) => { setPlans(r.plans || []); setSelected((r.plans || [])[0]?.id); }); }, []);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await powerPromote(slug, selected);
      toast.success(`Boost active until ${(r.featured_until || "").slice(0, 10)}`);
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't promote"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="promote-modal" className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-[#E4EAF3]">
          <div className="font-black text-xl text-[#0A1A2F]">Boost this petition</div>
          <div className="text-xs text-[#0A1A2F]/60 mt-0.5">MOCK checkout — no card charged. Real Stripe wires in Phase 20C.</div>
        </div>
        <div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto">
          {plans.map((pl) => (
            <button key={pl.id} data-testid={`promo-plan-${pl.id}`} onClick={() => setSelected(pl.id)}
              className={"w-full text-left p-4 rounded-xl border-2 transition-all " + (selected === pl.id ? "border-[#1E88FF] bg-[#1E88FF]/5" : "border-[#E4EAF3] bg-white hover:border-[#1E88FF]/50")}>
              <div className="flex items-center gap-2">
                <div className="font-black text-[#0A1A2F]">{pl.label}</div>
                <div className="ml-auto font-black text-[#1E88FF]">${pl.usd}</div>
              </div>
              <div className="text-xs text-[#0A1A2F]/60 mt-1">Placement: {pl.placement.join(" · ")}{pl.urgent ? " · urgent badge" : ""}</div>
            </button>
          ))}
        </div>
        <div className="p-5 border-t border-[#E4EAF3]">
          <button data-testid="promo-submit" onClick={submit} disabled={busy || !selected}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#F59E0B] to-[#B45309] text-white font-black py-3 rounded-full disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4" /> Confirm boost (mock)</>}
          </button>
        </div>
      </div>
    </div>
  );
}

